"use client";

import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";


export interface DatePickerProps {
  
  value: string;
  
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  
  fromDate?: string | Date;
  
  toDate?: string | Date;
}

const DISPLAY_FORMAT = "MMM d, yyyy";

function parseDateString(value: string): Date | undefined {
  if (!value || value.length !== 10) return undefined;
  const d = parse(value, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

function formatDateToValue(date: Date | undefined): string {
  if (!date) return "";
  return format(date, "yyyy-MM-dd");
}

export function DatePicker({
  value,
  onChange,
  id,
  placeholder = "Pick a date",
  disabled = false,
  required = false,
  className,
  fromDate,
  toDate,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const date = parseDateString(value);

  const from = React.useMemo(() => {
    if (!fromDate) return undefined;
    return typeof fromDate === "string" ? parseDateString(fromDate) ?? undefined : fromDate;
  }, [fromDate]);

  const to = React.useMemo(() => {
    if (!toDate) return undefined;
    return typeof toDate === "string" ? parseDateString(toDate) ?? undefined : toDate;
  }, [toDate]);

  const handleSelect = (selected: Date | undefined) => {
    onChange(formatDateToValue(selected));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "h-8 w-full justify-start text-left font-normal min-w-0",
            !date && "text-muted-foreground",
            className,
          )}
          aria-required={required}
          aria-label={placeholder}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">
            {date ? format(date, DISPLAY_FORMAT) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[110] w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          disabled={(d) => {
            if (from && d < from) return true;
            if (to && d > to) return true;
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}